import React from 'react';
import clsx from 'clsx';
import * as Headless from '@headlessui/react';
import { useRevalidator } from 'react-router-dom';

import { useUpdateLastViewedNewFeaturesMutation } from '~/api/graphql';
import { Dialog, DialogActions, DialogBody, DialogDescription } from '@oysterjs/ui/dialog';
import { Button } from '@oysterjs/ui/button';
import { ProLabel } from './pro-label';

interface Feature {
  title: string;
  launchedDate: Date;
  description: string;
  video: string;
  items: {
    title: React.ReactNode | string;
    description: React.ReactNode;
  }[];
}

const Pro = () => <ProLabel tooltip="Requires the Pro plan" />;

const allFeatures: Feature[] = [
  {
    title: 'Clients',
    launchedDate: new Date(2026, 9, 6, 0, 0, 0, 0),
    description:
      'Clients are your new way of working with your accounts. Every new workspace for a client starts with what Cara learned from your earlier work with them.',
    video: '/video/feature/2026-10-06-clients.mp4',
    items: [
      {
        title: 'Everything about a client, together',
        description:
          'Each client has its own page with their workspaces, the documents Cara generated for them, the files uploaded for them, and notes from your team.'
      },
      {
        title: 'Take the next step',
        description:
          'From a client’s page, move straight to what they need next, like a renewal, a certificate or a new quote, without re-explaining who they are.'
      }
    ]
  },
  {
    title: 'Memory',
    launchedDate: new Date(2026, 3, 29, 0, 0, 0, 0),
    description:
      'Cara’s learning is now explicit. Memory is a visible store of your preferences and your agency’s standards — saved as you work, applied whenever they’re relevant, and entirely yours to manage.',
    video: '/video/feature/2026-04-29-memory.mp4',
    items: [
      {
        title: 'Created from chat',
        description: (
          <>
            Tell Cara something worth remembering — <em>“remember that…”</em> — and it’s saved as a
            memory on the spot. No settings page or manual setup.
          </>
        )
      },
      {
        title: 'Applied automatically',
        description:
          'On every new conversation, Cara selects the memories that are relevant to what you’re asking and quietly uses them as context — no need to repeat yourself.'
      },
      {
        title: 'User and Organization scopes',
        description:
          'Personal memories stay yours and shape how Cara works for you. Organization memories apply to everyone in your agency — useful for shared standards like submission requirements or carrier rules. Admins manage org memories.'
      },
      {
        title: 'Dreams overnight',
        description:
          'Cara reviews recent conversations in the background, distilling new memories and clearing out unused ones, so the system gets more useful over time without any extra effort from you. Pin the memories that should stick around.'
      },
      {
        title: 'Manage your memories',
        description:
          'Visit Settings → Memory to see, pin, or delete any memory. You can also turn memory off entirely — existing memories are preserved but won’t be used until you turn it back on.'
      }
    ]
  },
  {
    title: 'Generate Word Documents',
    launchedDate: new Date(2026, 3, 12, 0, 0, 0, 0),
    description:
      'Cara can now create and deliver Word (.docx) documents directly in your workspace — no copy-paste required.',
    video: '/video/feature/2026-04-12-word-doc-generation.mp4',
    items: [
      {
        title: 'Ask Cara to generate a .docx',
        description:
          'Describe the document you need and Cara will produce a downloadable .docx file in your workspace.'
      },
      {
        title: 'Download and edit',
        description:
          'Download the generated .docx and open it in Word or Google Docs to make any final adjustments.'
      }
    ]
  },
  {
    title: 'Word Template Library',
    launchedDate: new Date(2026, 3, 12, 0, 0, 0, 0),
    description:
      "Upload your agency's .docx files to the Word Templates library and ask Cara to fill them in — just like HTML templates, but for Word documents.",
    video: '/video/feature/2026-04-12-word-template.mp4',
    items: [
      {
        title: 'Upload templates to the Library',
        description:
          'Go to Library → Word Templates and upload any .docx file. Add a title, description, and optional fill guidance so Cara knows how to use it.'
      },
      {
        title: 'Invoke by name in a workspace',
        description: 'Reference the template by name in your message.'
      },
      {
        title: 'Template file is never modified',
        description:
          'Cara uses the library file as a read-only source and produces a new downloadable .docx each time.'
      }
    ]
  },
  {
    title: 'Supplemental PDFs: Custom Forms Library',
    launchedDate: new Date(2026, 2, 28, 0, 0, 0, 0),
    description:
      'Supplemental PDF fills are now powered by the custom forms Library — more accurate and more convenient. Ad-hoc PDF uploads inside workspaces are no longer supported, so any custom supplemental templates you were using need to be migrated to the Library.',
    video: '/video/feature/2026-03-28-pdf-supplemental-library.mp4',
    items: [
      {
        title: 'More accurate, repeatable form fills',
        description:
          'Upload each supplemental form to the custom forms Library and configure it once — form filling guidance, field defaults, and any other settings. Cara applies those settings consistently every time the form is used, no more repeating yourself in the prompt.'
      },
      {
        title: 'Action required: migrate your custom presets',
        description:
          'Ad-hoc PDF uploads are no longer supported. If you had supplemental templates attached directly inside workspaces, add them to the custom forms Library instead. Once added, select them via the form picker next to the attachment icon in any message box.'
      }
    ]
  },
  {
    title: 'New Presets Available',
    launchedDate: new Date(2026, 2, 8, 0, 0, 0, 0),
    description:
      'Risk Matching and Coverage Gap Analysis are now available on all plans. Pro subscribers unlock five presets, including the new Risk Report, for deeper underwriting, claims, and risk analysis.',
    video: '/video/feature/2026-03-06-new-presets.mp4',
    items: [
      {
        title: 'Risk Matching',
        description:
          'Extract risk details from documents, enrich with web research, and identify market placement.'
      },
      {
        title: 'Coverage Gap Analysis',
        description:
          'Surface coverage gaps, verify exposure, and recommend additional lines of business.'
      },
      {
        title: (
          <>
            Risk Report <Pro />
          </>
        ),
        description:
          'Deliver a client-facing report with industry research, regulatory context, and risk mitigations.'
      },
      {
        title: (
          <>
            Underwriting Analysis <Pro />
          </>
        ),
        description:
          'Analyze commercial applications, verify insured information, and flag potential misrepresentation.'
      },
      {
        title: (
          <>
            SOV & COPE Analysis <Pro />
          </>
        ),
        description: 'Build or enrich Statements of Values with verified COPE information.'
      },
      {
        title: (
          <>
            Claims Review <Pro />
          </>
        ),
        description:
          'Review claims against policy terms, assess misrepresentation risk, and protect against E&O.'
      },
      {
        title: (
          <>
            Loss Run Analysis <Pro />
          </>
        ),
        description:
          'Compile loss history, research external risk signals, and produce a rate negotiation statement.'
      }
    ]
  },
  {
    title: 'Improved Workspace Searching and Filtering',
    launchedDate: new Date(2026, 2, 8, 0, 0, 0, 0),
    description:
      'The workspaces tab has been improved to make it easier to search, sort, and filter workspaces.',
    video: '/video/feature/2026-03-09-workspace-pagination.mp4',
    items: [
      {
        title: 'Faster workspaces tab',
        description:
          'Workspaces are now loaded progressively rather than all at once, making the workspaces tab much faster to load and much more responsive.'
      },
      {
        title: 'Sortable column headers',
        description:
          'You can sort workspaces in ascending or descending order by any column. Click the column header to sort by that column.'
      },
      {
        title: 'New search and filters',
        description:
          'Searching and filtering has been combined into a single search bar, allowing you search by title, client name, keywords, user, preset, status, date, and more. Simply start typing to see suggestions.'
      }
    ]
  },
  {
    title: 'Same Presets, New Use Cases',
    launchedDate: new Date(2026, 1, 22, 0, 0, 0, 0),
    description:
      'Presets now implement more specific use cases to make it easier to find the right preset for your needs.',
    video: '/video/feature/2026-02-22-preset-ui-update.mp4',
    items: [
      {
        title: 'Updated UI',
        description:
          'The Presets page has been updated to make the various use cases for each preset more visible and easier to understand.'
      },
      {
        title: 'Same Functionality',
        description:
          'All existing functionality remains exactly the same. You can now use these more specific buttons to trigger the preset with the exact use case you need.'
      },
      {
        title: 'Custom Presets',
        description:
          'Custom presets are also displayed after the built-in presets, and you can customize which presets are displayed using the settings "gear" icon in the top right above the presets.'
      }
    ]
  }
];

export const NewFeaturesModal = (props: { lastViewedNewFeatures?: Date | null }) => {
  const [open, setOpen] = React.useState(false);
  const [features, setFeatures] = React.useState<Feature[]>([]);
  const [featureIndex, setFeatureIndex] = React.useState(0);
  const [progress, setProgress] = React.useState(0);
  const [updateLastViewedNewFeatures] = useUpdateLastViewedNewFeaturesMutation();
  const { revalidate } = useRevalidator();

  const onClose = async () => {
    setOpen(false);
    await updateLastViewedNewFeatures();
    revalidate();
  };

  React.useEffect(() => {
    if (!props.lastViewedNewFeatures) {
      setFeatures(allFeatures);
      setOpen(true);
      setFeatureIndex(0);
      return;
    }

    const lastViewed = props.lastViewedNewFeatures ? new Date(props.lastViewedNewFeatures) : null;
    const unseenFeatures = lastViewed
      ? allFeatures.filter((feature) => feature.launchedDate > lastViewed)
      : [];

    if (unseenFeatures.length > 0) {
      setFeatures(unseenFeatures);
      setOpen(true);
      setFeatureIndex(0);
    }
  }, [props.lastViewedNewFeatures]);

  React.useEffect(() => {
    setProgress(0);
  }, [featureIndex]);

  const feature = features[featureIndex];

  return (
    <Dialog open={open} onClose={onClose} size="3xl">
      <h1 className="text-sm font-medium text-primary-500 dark:text-primary-400 uppercase mb-0">
        What's New
      </h1>
      <h2 className="text-2xl font-semibold text-neutral-900 dark:text-white mt-1">
        {features[featureIndex]?.title}
      </h2>
      <DialogDescription>{features[featureIndex]?.description}</DialogDescription>
      <DialogBody>
        <div className="flex flex-col gap-8 md:flex-row">
          <div className="relative bg-neutral-50 dark:bg-neutral-900 rounded-lg overflow-clip w-full md:w-1/2 min-h-[12rem] flex flex-col">
            {feature && (
              <>
                <video
                  src={feature.video}
                  onTimeUpdate={(event) =>
                    setProgress(
                      Math.floor(
                        (event.currentTarget.currentTime / event.currentTarget.duration) * 100
                      ) || 0
                    )
                  }
                  autoPlay
                  muted
                  loop
                  className="w-full"
                />
                <div className="absolute bottom-0 left-0 h-0.5 bg-neutral-100 dark:bg-neutral-700 rounded-full w-full">
                  <div
                    className="h-full bg-primary-500/75 rounded-full transition-all duration-300 ease-linear"
                    style={{
                      width: `${progress}%`
                    }}
                  />
                </div>
              </>
            )}
          </div>
          <div className="w-full md:w-1/2">
            <ul>
              {features[featureIndex]?.items.map((item, index) => (
                <li className="text-sm mb-3" key={`${featureIndex}-${index}`}>
                  <div className="inline-flex items-center font-medium">{item.title}</div>
                  <div className="text-neutral-700 dark:text-neutral-300 mt-1">
                    {item.description}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
        {features.length > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            {features.map((_, index) => (
              <Headless.Button
                key={index}
                className={clsx(
                  'size-2 bg-neutral-200 dark:bg-neutral-700 rounded-full hover:bg-neutral-300 dark:hover:bg-white/20',
                  featureIndex === index ? 'bg-primary-500 hover:bg-primary-500' : ''
                )}
                onClick={() => setFeatureIndex(index)}
              />
            ))}
          </div>
        )}
      </DialogBody>
      <DialogActions>
        {features.length > 1 && featureIndex > 0 && (
          <Button color="white" onClick={() => setFeatureIndex(featureIndex - 1)}>
            Previous
          </Button>
        )}
        {features.length > 1 && featureIndex < features.length - 1 && (
          <Button color="sky" onClick={() => setFeatureIndex(featureIndex + 1)}>
            Next
          </Button>
        )}
        {featureIndex === features.length - 1 && (
          <Button color="sky" onClick={onClose}>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
